import { getServerUri } from "./uriUtility";

/**
 * The admin screens' API surface — user management and, for a superAdmin,
 * companies and base grants.
 *
 * Every endpoint here is form-encoded, and array fields are sent as REPEATED
 * fields (`bases=a&bases=b`) because that is what the server's parser turns back
 * into an array. Note the sharp edge that shape has: a single value arrives as a
 * bare string rather than a one-element array, which is why the server reads
 * these through userRequestFields.js rather than assuming an array.
 */

function body(params) {
  const search = new URLSearchParams();
  search.append("accessToken", localStorage.getItem("session.accessToken"));

  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)));
    } else {
      search.append(key, String(value));
    }
  }
  return search;
}

async function post(servicePath, params, { method = "POST" } = {}) {
  const response = await fetch(getServerUri(servicePath), {
    method,
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body(params),
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = text;
  }

  return { ok: response.status === 200, status: response.status, data };
}

/**
 * The one multipart call in this module: a logo is a file, not a form field.
 * FormData sets its own content-type boundary, so no header is passed here.
 */
export async function uploadCompanyLogo(companyID, file) {
  const form = new FormData();
  form.append("accessToken", localStorage.getItem("session.accessToken"));
  form.append("companyID", String(companyID));
  form.append("logo", file);

  const response = await fetch(getServerUri("company/uploadCompanyLogo"), { method: "POST", body: form });
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = text;
  }
  return { ok: response.status === 200, status: response.status, data };
}

// A permission list a client can render without keeping its own copy of the
// vocabulary in sync by hand. The order is the order the checkboxes appear in.
export const PERMISSION_OPTIONS = [
  { key: "viewContent", label: "View content", hint: "See versions and assets" },
  { key: "editContent", label: "Edit content", hint: "Change configuration and assets" },
  { key: "manageAssets", label: "Manage assets", hint: "Upload images, atlases, fonts, sounds, video" },
  { key: "createVersion", label: "Create versions", hint: "Create, duplicate and organise versions" },
  { key: "deleteContent", label: "Delete content", hint: "Delete versions and assets" },
  // Not "export": the same permission now covers pulling a version INTO a
  // snapshot pool, so the label has to name both directions or it reads as
  // narrower than it is.
  { key: "exportPlayable", label: "Import/export playables", hint: "Build, preview, and move playables in and out" },
];

/**
 * Playin's own tools, offered only to a company marked hasInternalTools.
 *
 * Neither is company-scoped: Version Update rewrites every version of any base
 * it is given and can target another environment's database, and Version
 * Transfer moves whole versions between installs. The server refuses both for a
 * company without the flag; these are kept separate so the checkboxes are not
 * even shown there.
 */
/**
 * The version editor's two tab sets, for the screen that narrows a user to some
 * of them. Mirrors controller/user/editorTabs.js — the server checks what it is
 * sent against the same vocabulary, so a key added in one place has to be added
 * in the other.
 *
 * Agent is in both sets and is opt-in: a user with no list here does not get it.
 * Nothing else grants it — ticking it is what gives somebody the agent, and the
 * run is billed to that user's own company.
 */
export const LEGACY_TAB_OPTIONS = [
  { key: "versionSettings", label: "Version Settings" },
  { key: "localization", label: "Localization" },
  { key: "generalSettings", label: "General Settings" },
  { key: "agent", label: "Agent" },
];

export const PLAY_ENGINE_TAB_OPTIONS = [
  { key: "versionSettings", label: "Version Settings" },
  { key: "assets", label: "Assets" },
  { key: "generalSettings", label: "General Settings" },
  { key: "customVariables", label: "Custom Variables" },
  { key: "design", label: "Design" },
  { key: "sounds", label: "Sounds" },
  { key: "scripts", label: "Scripts" },
  { key: "agent", label: "Agent" },
];

// A pe3d base's Agent tab follows PLAY_ENGINE_TAB_OPTIONS' "agent".
export const PE3D_TAB_OPTIONS = [
  { key: "config", label: "Config" },
  { key: "playable", label: "Playable" },
  { key: "assets", label: "Assets" },
  { key: "design", label: "Design" },
  { key: "release", label: "Release" },
];

export const INTERNAL_PERMISSION_OPTIONS = [
  { key: "transferVersion", label: "Transfer versions", hint: "Export and import version bundles between installs" },
  { key: "bulkUpdateDatabase", label: "Bulk database update", hint: "Version Update — rewrites every version of a base" },
];

export const ROLE_OPTIONS = [
  { key: "member", label: "Member", hint: "Works on content, according to its permissions" },
  { key: "companyAdmin", label: "Company admin", hint: "Also manages this company's users and billing" },
  { key: "superAdmin", label: "Super admin", hint: "Manages every company, and can switch into any" },
];

export const findUsers = (params) => post("user/find", params);
export const insertUser = (params) => post("user/insert", params);
export const updateUser = (params) => post("user/updateOne", params);
export const deleteUser = (params) => post("user/delete", params, { method: "DELETE" });

export const findCompanies = (params) => post("company/find", params);
export const insertCompany = (params) => post("company/insert", params);
export const updateCompany = (params) => post("company/updateOne", params);
export const switchCompany = (companyID) => post("company/switch", { companyID });

export const findAllBases = () => post("base/findAll", {});
// Every base in the install, for the company grant picker — asked for by name
// so it also works for a user granted grantBasesToCompanies (base/findAll.js).
export const findGrantableBases = () => post("base/findAll", { scope: "install" });

// Runtime playEngine bases and games. The existing manageRuntimeBases grant
// covers creating both; deleting stays superAdmin-only. Legacy documents come
// from git.
export const findRuntimeBases = () => post("base/findRuntime", {});
export const findAllGames = () => post("game/findAll", {});
export const findRuntimeGames = () => post("game/findRuntime", {});
export const deleteGame = (gameID) => post("game/deleteOne", { gameID }, { method: "DELETE" });
export async function insertGame(title, logo) {
  const form = new FormData();
  form.append("accessToken", localStorage.getItem("session.accessToken"));
  form.append("title", title);
  form.append("logo", logo);
  const response = await fetch(getServerUri("game/insert"), { method: "POST", body: form });
  const responseText = await response.text();
  let data = responseText;
  try { data = JSON.parse(responseText); } catch (_error) { /* Error codes are plain text. */ }
  return { ok: response.status === 200, status: response.status, data };
}
export const listBaseFlows = () => post("base/listFlows", {});
export const insertBase = (params) => post("base/insert", params);
export const renameBase = (baseID, title) => post("base/renameOne", { baseID, title });
export const deleteBase = (baseID) => post("base/deleteOne", { baseID }, { method: "DELETE" });
export const deleteAllVersionsOfBase = (baseID) =>
  post("versionPlayEngine/deleteAllOfBase", { baseID }, { method: "DELETE" });
